import React, { ChangeEvent, useEffect, useState } from 'react'
import 'leaflet/dist/leaflet.css'
import { LatLng } from 'leaflet'
import AddIcon from '@mui/icons-material/Add'

import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Link,
  Paper,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  Tabs,
  Theme,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material'
import { makeStyles } from '@mui/styles'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import CloseIcon from '@mui/icons-material/Close'
import EditIcon from '@mui/icons-material/Edit'
import TableViewIcon from '@mui/icons-material/TableView'
import BungalowIcon from '@mui/icons-material/Bungalow'
import CollectionsIcon from '@mui/icons-material/Collections'
import FormatListNumberedIcon from '@mui/icons-material/FormatListNumbered'

import { useDispatch, useSelector } from 'react-redux'
import { getUser, getUserPermissions } from '../../../store/settings/settings.selectors'
import {
  BookedEventPriceCategory,
  Entity,
  EventMember,
  EventPriceCategoryResponse,
  EventResultObject,
  FetchResponse,
  Member,
  MemberResponse,
  Message,
  Method,
  PermissionType,
  User,
  UserPermissions,
  UserRole,
} from '../../../shared/types'
import formatDate from '../../../util/formatDate'
import LocationMap from '../../../shared/components/Location/LocationMap'
import { EventObject } from '../../../screens/App/Events/EventComponent'
import TagComponent from '../../../shared/components/TagComponent'
import MediaCarousel from '../../../shared/components/MediaCarousel'
import MediaUploadComponent from '../../../shared/components/MediaUploadComponent'
import MediaGalleryComponent from '../../../shared/components/MediaGallery/MediaGalleryComponent'

import DropDownComponent, { DropDownElement } from '../../../shared/components/DropDownComponent'
import { getEventPriceCategories, getEventResults, getMembers } from '../../../store/data/data.selectors'
import fetchData from '../../../fetch/fetchData'
import { getApiVersion } from '../../../shared/config/getApiVersion'
import * as dataActions from '../../../store/data/data.actions'
import * as settingsActions from '../../../store/settings/settings.actions'
import Env from '../../../shared/config/envConfig'
import EventResults from './EventResults/EventResults'
import TextInputComponent, { TextFieldType } from '../../../shared/components/TextInputComponent'
import ButtonComponent from '../../../shared/components/ButtonComponent'
import DatePickerComponent from '../../../shared/components/DatePickerComponent'
import getDateFromCustomDate from '../../../util/getDateFromCustomDate'
import DeleteIcon from '@mui/icons-material/Delete'
import getCustomDateFromDate from '../../../util/getCustomDateFromDate'
import DOMPurify from 'dompurify'
import getAgeOfMemberAtDate from '../../../util/getAgeOfMemberAtDate'
import getPriceForEventMember from '../../../util/getPriceForEventMember'
import formatCurrency from '../../../util/formatCurrency'
import getPriceForAllEventMembers from '../../../util/getPriceForAllEventMembers'
import CardIcon from '../../../shared/assets/icons/CardIcon'
import CheckBoxComponent from '../../../shared/components/CheckBoxComponent'
import getDefaultEventPriceCategories from '../../../util/getDefaultEventPriceCategories'
import getTextAsFormattedString from '../../../util/getTextAsFormattedString'
import { addCsrfHeaders } from '../../../shared/config/csrf'

interface Props {
  data: EventObject
  setAdminMode: (value: boolean) => void
}

enum TabEnum {
  overview = 'overview',
  location = 'location',
  participation = 'participation',
  registration = 'registration',
  gallery = 'gallery',
  results = 'results'
}

const SingleEvent: React.FC<Props> = (props) => {
  const theme: Theme = useTheme()
  const downMd = useMediaQuery(theme.breakpoints.down('md'))

  const dispatch = useDispatch()

  const useStyles = makeStyles((theme: Theme) => ({
    paper: {
      margin: downMd ? '0px' : '24px 24px 24px 0',
      padding: downMd ? '0px 8px' : '24px',
      width: '100%',
    },
    location: {
      textTransform: 'uppercase',
      opacity: '0.8',
      width: '100%',
      whiteSpace: 'nowrap',
      overflow: 'hidden',
    },
    description: {
      cursor: 'pointer',
      textTransform: 'uppercase',
      opacity: '0.8',
      whiteSpace: 'nowrap',
      overflow: 'hidden',
    },
    container: {
      marginTop: theme.spacing(4),
    },
    image: {
      width: '100%',
      height: 'auto',
      cursor: 'pointer',
    },
    tableHeader: {
      fontWeight: 'bold',
      textAlign: 'left',
    },
  }))

  const classes = useStyles()

  const userPermissions: UserPermissions = useSelector(getUserPermissions)
  const user: User | undefined = useSelector(getUser)
  const eventResults: EventResultObject | undefined = useSelector(getEventResults)

  const basePath = Env.restBase
  const fetchHeaders = Env.fetchHeaders
  addCsrfHeaders(fetchHeaders)

  const setEventResults = (res: FetchResponse) => {
    dispatch(dataActions.setEventResult({ ...eventResults, [props.data.event.id as string]: res.results }))
  }

  const [message, setMessage] = useState<Message>()

  useEffect(() => {
    message && dispatch(settingsActions.setMessage(message))
  }, [message])

  useEffect(() => {
    userPermissions.eventResults.find(elem => elem === PermissionType.read) &&
    props.data.event.id && props.data.event.id !== 'new' && !eventResults[props.data.event.id] &&
    fetchData(basePath, getApiVersion(), fetchHeaders, Entity.eventResults, Method.GET, props.data.event.id, undefined, setEventResults, setMessage)
  }, [props.data, props.data.event.id])

  const getMyEventMember = () => {
    const eventMemberFromUser: EventMember[] = []
    activeEvent.eventMember.map(eventMember => {
      if (eventMember.memberId === user?.memberId || eventMember.registeredByMemberId === user?.memberId) {
        eventMember.from = eventMember.from || activeEvent.event.eventFrom || {}
        eventMember.to = eventMember.to || activeEvent.event.eventTo || {}
        eventMemberFromUser.push(eventMember)
      }
    })
    return eventMemberFromUser
  }

  const members: MemberResponse | undefined = useSelector(getMembers)
  const eventPriceCategories: EventPriceCategoryResponse | undefined = useSelector(getEventPriceCategories)

  const [showNewRegistration, setShowNewRegistration] = React.useState<boolean>(false)
  const [rowsPerPage, setRowsPerPage] = React.useState<number>(10)
  const [actualPage, setActualPage] = React.useState<number>(0)
  const [activeTab, setActiveTab] = React.useState<TabEnum>(window.localStorage.getItem('activeEventTab') as TabEnum)
  const [activeEvent, setActiveEvent] = React.useState<EventObject>(props.data)

  const [myEventMember, setMyEventMember] = React.useState<EventMember[]>(getMyEventMember())

  useEffect(() => {
    setMyEventMember(getMyEventMember())
  }, [activeEvent, activeEvent.eventMember])

  useEffect(() => {
    if (activeEvent.event.id === props.data.event.id) {
      setActiveTab(window.localStorage.getItem('activeEventTab') ? window.localStorage.getItem('activeEventTab') as TabEnum : TabEnum.location)
    } else {
      window.localStorage.removeItem('activeEventTab')
      setActiveTab(TabEnum.location)
    }
    setActiveEvent(props.data)
  }, [props])

  const handleChangePage = (e: React.MouseEvent<HTMLButtonElement> | null, page: number) => {
    setActualPage(page)
  }
  const handleChangePageSize = (e: ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(Number.parseInt(e.target.value))
  }

  const getPriceOptions = (memberId: string) => {
    const member = members?.results.find(elem => elem.originId === memberId)
    const memberAge = member && props.data.event.eventFrom && getAgeOfMemberAtDate(member, props.data.event.eventFrom)
    const options: DropDownElement[] = []
    eventPriceCategories?.results.map((priceCategory) => {
      const eventMem = myEventMember.find(member => member.memberId === memberId)
      const eventMemPrice = eventMem && eventMem.eventPriceCategories.find(price => price.id === priceCategory.id)
      if (!eventMemPrice) {
        if ((priceCategory.ageFrom && memberAge && priceCategory.ageFrom <= memberAge) || !priceCategory.ageFrom) {
          if ((priceCategory.ageTo && memberAge && priceCategory.ageTo >= memberAge) || !priceCategory.ageTo) {
            priceCategory.id && options.push({
              key: priceCategory.id,
              value: priceCategory.title,
            })
          }
        }
      }
    })
    return options
  }

  const getMemberOptions = () => {
    const options: DropDownElement[] = []
    members?.results.map((tempMember: Member) => {
      // prüfen ob bereits in eventMemberListe enthalten
      if (!tempMember.dead) {
        if (!myEventMember.find(eM => eM.memberId === tempMember.originId) && !activeEvent.eventMember.find(eM => eM.memberId === tempMember.originId)) {
          tempMember.originId && options.push({
            key: tempMember.originId,
            value: `${tempMember.firstname} ${tempMember.lastname}`,
          })
        }
      }
    })
    options.sort((a, b) => {
      const nameA = a.value.toUpperCase()
      const nameB = b.value.toUpperCase()
      if (nameA < nameB) {
        return -1
      }
      if (nameA > nameB) {
        return 1
      }
      return 0
    })
    return options
  }

  const getFilteredEventMember = (eventMember: EventMember[]): EventMember[] => {
    let end = actualPage * rowsPerPage + rowsPerPage
    if (end > eventMember.length) {
      end = eventMember.length
    }
    const filteredMember = eventMember.slice(actualPage * rowsPerPage, end)
    return filteredMember
  }

  const editRegistration = (myEventMember: EventMember[]) => {
    if (myEventMember.length === 0) {
      const newEventMem = [...myEventMember]
      activeEvent.event.id && user && user.memberId && newEventMem.push({
        id: 'new',
        memberId: '',
        eventId: activeEvent.event.id,
        participate: true,
        gamer: false,
        registeredByMemberId: user.memberId,
        eventPriceCategories: [],
        from: props.data.event.eventFrom || {},
        to: props.data.event.eventTo || {},
        notes: '',
      })
      setMyEventMember(newEventMem)
    }
    setShowNewRegistration(true)
  }

  const getParticipationTable = () => {
    return (
      <>
        <Table size='small'>
          <TableHead>
            <TableRow>
              <TableCell className={classes.tableHeader} sx={{ width: '20px' }}>
                &nbsp;
              </TableCell>
              <TableCell className={classes.tableHeader}>
                Vorname
              </TableCell>
              <TableCell className={classes.tableHeader}>
                Nachname
              </TableCell>
              {!downMd &&
                <>
                  <TableCell className={classes.tableHeader}>
                    Anreise
                  </TableCell>
                  <TableCell className={classes.tableHeader}>
                    Abreise
                  </TableCell>
                </>}
              <TableCell className={classes.tableHeader} sx={{ width: '20px' }}>
                &nbsp;
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {getFilteredEventMember(activeEvent?.eventMember)?.map((member: EventMember) => {
              return (
                <TableRow key={member.id}>
                  <TableCell sx={{ height: '48px' }}>
                    {member.gamer &&
                      <CardIcon width='16px' />}
                  </TableCell>
                  <TableCell>
                    {members?.results.find(elem => elem.originId === member.memberId)?.firstname}
                  </TableCell>
                  <TableCell>
                    {members?.results.find(elem => elem.originId === member.memberId)?.lastname}
                  </TableCell>
                  {!downMd &&
                    <>
                      <TableCell>
                        {formatDate(member.from)}
                      </TableCell>
                      <TableCell>
                        {formatDate(member.to)}
                      </TableCell>
                    </>}
                  <TableCell className={classes.tableHeader} sx={{ width: '20px' }}>
                    {(member.memberId === user?.memberId || member.registeredByMemberId === user?.memberId) && (activeEvent.event.eventTo && getDateFromCustomDate(activeEvent.event.eventTo) && getDateFromCustomDate(activeEvent.event.eventTo)?.getMilliseconds() > new Date().getMilliseconds()) &&
                      <IconButton onClick={() => {
                        editRegistration(myEventMember)
                      }}
                      ><EditIcon />
                      </IconButton>}
                  </TableCell>
                </TableRow>)
            }
            )}
          </TableBody>
        </Table>
        <TablePagination
          component='div'
          count={activeEvent.eventMember.length}
          rowsPerPage={rowsPerPage}
          page={actualPage}
          rowsPerPageOptions={downMd ? [] : [10, 25, 50, 100]}
          backIconButtonProps={{
            'aria-label': 'voherige Seite',
          }}
          nextIconButtonProps={{
            'aria-label': 'nächste Seite',
          }}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangePageSize}
        />
      </>
    )
  }

  const setEventMember = (data: FetchResponse) => {
    dispatch(dataActions.setEventMember({ count: data.count, results: data.results }))
  }

  let registrationOpen = false
  if (activeEvent.event.registrationUntil && getDateFromCustomDate(activeEvent.event.registrationUntil)) {
    const registrationDate = getDateFromCustomDate(activeEvent.event.registrationUntil)
    if (registrationDate && registrationDate > new Date()) {
      registrationOpen = true
    }
  }

  const SingleEventContent = () => {
    const stringCoords = activeEvent?.eventLocation?.coordinates?.split(',')
    const coordinates: number[] = []
    stringCoords && stringCoords[0] && coordinates.push(parseInt(stringCoords[0], 10))
    stringCoords && stringCoords[1] && coordinates.push(parseInt(stringCoords[1], 10))
    return (
      <>
        <div className={classes.location}>
          <Box sx={{ display: 'flex', justifyContent: 'Spave-between', flexWrap: 'nowrap' }}>
            <Typography
              variant='h5'
              style={{ fontWeight: '501' }}
            >
              {activeEvent.event.title}
            </Typography>
            {userPermissions.events.find(elem => elem === PermissionType.update) && downMd &&
              <EditIcon
                sx={{
                  color: theme.palette.primary.main,
                  position: 'absolute',
                  right: '30px',
                  zIndex: 100,
                  cursor: 'pointer',
                }}
                onClick={() => props.setAdminMode(true)}
              />}
          </Box>
          <Typography
            variant='body1'
          >{formatDate(activeEvent.event.eventFrom)} - {formatDate(activeEvent.event.eventTo)}
          </Typography>
        </div>

        {/* Tabnavigation */}
        <Tabs
          sx={{ marginBottom: '20px' }}
          value={activeTab}
          onChange={(e, newValue) => {
            window.localStorage.setItem('activeEventTab', newValue)
            setActiveTab(newValue)
          }}
        >
          {(activeEvent.event.description) &&
            <Tab
              sx={{ minWidth: '70px!important' }}
              key={TabEnum.overview}
              value={TabEnum.overview}
              label={downMd ? undefined : 'Skatturnier'}
              icon={downMd ? (
                <CardIcon
                  width='20px'
                  color={activeTab === TabEnum.results ? theme.palette.primary.main : theme.palette.grey.A700}
                />)
                : undefined}
            />}
          <Tab
            sx={{ minWidth: '70px!important' }}
            key={TabEnum.location}
            value={TabEnum.location}
            label={downMd ? undefined : 'Unterkunft'}
            icon={downMd ? <BungalowIcon /> : undefined}
          />
          {(registrationOpen || (user && user.userRole === UserRole.admin))
            ? (
              <Tab
                sx={{ minWidth: '70px!important' }}
                key={TabEnum.registration}
                value={TabEnum.registration}
                icon={downMd ? <EditIcon /> : undefined}
                label={downMd ? undefined : 'Anmeldung'}
              />)
            : (
              <Tab
                sx={{ minWidth: '70px!important' }}
                key={TabEnum.participation}
                value={TabEnum.participation}
                icon={downMd ? <EditIcon /> : undefined}
                label={downMd ? undefined : 'Teilnehmer'}
              />)}
          <Tab
            sx={{ minWidth: '70px!important' }}
            key={TabEnum.results}
            value={TabEnum.results}
            icon={downMd ? <FormatListNumberedIcon /> : undefined}
            label={downMd ? undefined : 'Ergebnisse'}
          />
          <Tab
            sx={{ minWidth: '70px!important' }}
            key={TabEnum.gallery}
            value={TabEnum.gallery}
            icon={downMd ? <CollectionsIcon /> : undefined}
            label={downMd ? undefined : 'Galerie'}
          />
        </Tabs>

        {// OVERVIEW
          activeTab === TabEnum.overview &&
            <Grid container item xs={12} spacing={2}>
              <Grid item xs={12}>
                <Typography variant='h6'>{activeEvent.event.title}</Typography>
              </Grid>
              <Grid item xs={12}>
                {activeEvent.event.eventTo?.year &&
                  <img width='100%' src={`/eventImages/${activeEvent.event.eventTo?.year}.png`} alt='' />}
                <Typography
                  variant='body1'
                  dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(getTextAsFormattedString(activeEvent.event.description?.trim() || '')) }}
                />
                {activeEvent.event.signature &&
                  <Box sx={{ textAlign: 'right', fontFamily: 'Allura', fontSize: '24px' }}>
                    {activeEvent.event.signature}
                  </Box>}
              </Grid>
            </Grid>
        }

        {// LOCATION
          activeTab === TabEnum.location &&
            <Grid container item xs={12} spacing={2}>
              <Grid item xs={12}>
                <Typography variant='h6'>{activeEvent.eventLocation?.name}</Typography>
                <Typography variant='caption'>
                  <Link
                    style={{ textDecoration: 'none' }}
                    color={theme.palette.grey[600]}
                    href={activeEvent.eventLocation?.linkUrl}
                    className={classes.description}
                    target='_blank'
                  >{activeEvent.eventLocation?.caption}
                  </Link>
                </Typography>
              </Grid>
              {downMd &&
                <Grid item xs={12} width='100%'>
                  <MediaCarousel
                    items={activeEvent.eventLocation?.imageUrls || []}
                    maxWidth='100%'
                    height={(window.screen.width) + 3 / 4}
                    navButtonsAlwaysVisible
                  />
                </Grid>}
              <Grid item xs={downMd ? 12 : 6}>
                <Typography
                  variant='body1'
                  dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(getTextAsFormattedString(activeEvent.eventLocation?.description)) }}
                />
                {activeEvent.event.signature &&
                  <Box sx={{ textAlign: 'right', fontFamily: 'Allura', fontSize: '24px' }}>
                    {activeEvent.eventLocation?.signature}
                  </Box>}
              </Grid>
              {downMd && activeEvent?.eventLocation?.coordinates &&
                <Grid item xs={12}>
                  <LocationMap
                    label={[activeEvent?.eventLocation?.name]}
                    mapZoom={12}
                    positions={[new LatLng(coordinates[0], coordinates[1])]}
                  />
                </Grid>}
              {!downMd &&
                <Grid item xs={6} container spacing={2}>
                  <Grid item xs={12}>
                    <MediaCarousel
                      items={activeEvent.eventLocation?.imageUrls || []}
                      maxWidth='100%'
                      height={(2 * window.screen.width) / 9}
                      navButtonsAlwaysVisible
                    />
                  </Grid>
                  {activeEvent?.eventLocation?.coordinates &&
                    <Grid item xs={12}>
                      <LocationMap
                        label={[activeEvent?.eventLocation?.name]}
                        mapZoom={12}
                        positions={[new LatLng(coordinates[0], coordinates[1])]}
                      />
                    </Grid>}
                </Grid>}
            </Grid>
        }
        {// REGISTRATION
          activeTab === TabEnum.registration &&
            <>
              <Box
                sx={{ display: 'flex', width: '100%', justifyContent: 'space-between', alignItems: 'center' }}
              >
                <Box
                  sx={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'center' }}
                >
                  <Typography variant='h6'>Bisherige Meldungen</Typography>
                  {user?.userRole === UserRole.admin &&
                    <IconButton
                      onClick={() => {
                        const restUrl = `${basePath}/api/export/eventMember/${activeEvent.event.id}`
                        fetch(restUrl,
                          {
                            method: 'GET',
                            credentials: 'include',
                          })
                          .then(response => response.blob())
                          .then(blob => {
                            const url = window.URL.createObjectURL(blob)
                            const a = document.createElement('a')
                            a.href = url
                            a.target = '_blank'
                            a.download = `Anmeldeliste${activeEvent.event.eventFrom?.year}_${Date.now()}.xlsx`
                            document.body.appendChild(a)
                            a.click()
                            a.remove()
                          })
                      }}
                    ><TableViewIcon />
                    </IconButton>}
                </Box>
                {/* Add new member */}
                <IconButton
                  onClick={() => {
                    editRegistration(myEventMember)
                  }}
                ><AddIcon />
                </IconButton>
              </Box>
              {getParticipationTable()}
            </>
        }
        {// GALLERY
          activeTab === TabEnum.gallery &&
            <>
              <MediaGalleryComponent
                mediaData={[]}
                setSelected={(data) => console.log(data)}
                useRoundImageBorders
              />
              <MediaUploadComponent
                onlyUploadButton
                loading={false}
                minCropBoxHeight={170}
                minCropBoxWidth={170}
                aspectRatio={1}
              />
              <TagComponent options={[{ id: '001', label: 'Robi' }]} label='Tags' />
            </>
        }
        {// RESULTS
          activeTab === TabEnum.results &&
            <>
              {props.data.event.id &&
                <EventResults
                  eventMember={activeEvent.eventMember}
                  eventId={props.data.event.id}
                />}
            </>
        }
        {// PARTICIPATION
          activeTab === TabEnum.participation &&
            <>
              <Box
                sx={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'center', flexWrap: 'nowrap' }}
              >
                <Typography variant='h6' sx={{ marginRight: '10px' }}>Anmeldungen</Typography>
              </Box>
              {getParticipationTable()}
            </>
        }
        <Dialog
          open={showNewRegistration} onClose={() => setShowNewRegistration(false)}
          sx={{ '& .MuiDialog-paper': { borderRadius: 0, minWidth: '80%', maxWidth: '80%' } }}
        >
          <Stack sx={{ p: 3 }}>
            <Stack
              direction='row' spacing={3} alignItems='center'
              justifyContent='space-between'
            >
              <DialogTitle sx={{ fontWeight: 600, p: 0, fontSize: '16px', marginBottom: '20px' }}>
                Neue Anmeldung
              </DialogTitle>
            </Stack>
            <DialogContent>
              <Grid container>
                {myEventMember.map((eM: EventMember, index) => {
                  const selectedMember = members?.results.find(member => member.originId === eM.memberId)
                  return (
                    <>
                      {eM.memberId
                        ? (
                          <Box
                            sx={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'flex-start',
                              width: '100%',
                              marginBottom: '20px',
                            }}
                          >
                            <Accordion sx={{ width: '100%' }}>
                              <AccordionSummary
                                expandIcon={<ExpandMoreIcon />}
                                id={eM.memberId}
                              >
                                <Box
                                  sx={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    width: '100%',
                                  }}
                                >
                                  <Box
                                    sx={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'center' }}
                                  >
                                    {eM.gamer
                                      ? (
                                        <CardIcon
                                          color={theme.typography.body1.color}
                                          width='16px'
                                        />)
                                      : (
                                        <Box sx={{ marginLeft: '8px' }}>&nbsp;</Box>)}
                                    <Typography
                                      sx={{ marginLeft: '8px' }}
                                      variant='body1'
                                    ><b>{`${selectedMember && selectedMember.firstname} ${selectedMember && selectedMember.lastname}`}</b>
                                    </Typography>
                                  </Box>
                                  <Typography
                                    variant='body1'
                                    sx={{ marginRight: '12px' }}
                                  ><b>{formatCurrency(getPriceForEventMember(eM, eventPriceCategories?.results))}</b>
                                  </Typography>
                                </Box>
                              </AccordionSummary>
                              <AccordionDetails sx={{ marginLeft: '20px' }}>
                                <Grid container spacing={2}>
                                  <Grid item xs={12}>
                                    <CheckBoxComponent
                                      value={eM.gamer}
                                      label='Skatspieler'
                                      onChange={(value) => {
                                        const newEventMem = [...myEventMember]
                                        newEventMem[index].gamer = value
                                        setMyEventMember(newEventMem)
                                      }}
                                    />
                                  </Grid>
                                  <Grid item xs={3}>
                                    <DatePickerComponent
                                      onChangeDate={(value) => {
                                        const newEventMem = [...myEventMember]
                                        newEventMem[index].from = value ? getCustomDateFromDate(value) : {}
                                        setMyEventMember(newEventMem)
                                      }}
                                      value={myEventMember[index].from && getDateFromCustomDate(myEventMember[index].from)}
                                      label='Anreise'
                                    />
                                  </Grid>
                                  <Grid item xs={3}>
                                    <DatePickerComponent
                                      onChangeDate={(value) => {
                                        const newEventMem = [...myEventMember]
                                        newEventMem[index].to = value ? getCustomDateFromDate(value) : {}
                                        setMyEventMember(newEventMem)
                                      }}
                                      value={myEventMember[index].to && getDateFromCustomDate(myEventMember[index].to)}
                                      label='Abreise'
                                    />
                                  </Grid>
                                  <Grid item xs={6}>
                                    <TextInputComponent
                                      margin='none'
                                      fullWidth
                                      onChange={(value) => {
                                        const newEventMem = [...myEventMember]
                                        newEventMem[index].notes = value?.toString()
                                        setMyEventMember(newEventMem)
                                      }}
                                      label='Anmerkungen'
                                    />
                                  </Grid>
                                </Grid>
                                <Typography variant='body1'>
                                  <b>
                                    Gebuchte Optionen:
                                  </b>
                                </Typography>
                                {/* Optionen, wenn Member ausgewählt */}
                                {myEventMember[index].memberId &&
                                  <>
                                    {myEventMember[index].eventPriceCategories.map((evPriceCat, priceIndex) => {
                                      return (
                                        <Box key={priceIndex}>
                                          {evPriceCat.quantity > 0
                                            ? (
                                              <Grid container spacing={2}>
                                                <Grid item xs={6}>
                                                  <Typography variant='body1'>
                                                    {eventPriceCategories?.results.find(elem => elem.id === evPriceCat.eventPriceCategoryId)?.title}
                                                  </Typography>
                                                </Grid>
                                                <Grid item xs={3}>
                                                  <TextInputComponent
                                                    margin='none'
                                                    label='Anzahl'
                                                    required
                                                    value={evPriceCat.quantity || 0}
                                                    type={TextFieldType.number}
                                                    min={1}
                                                    fullWidth
                                                    size='small'
                                                    onChange={(value?: string | number) => {
                                                      const newValue: number = parseInt(value as string, 10)
                                                      const newEventMem: EventMember[] = []
                                                      myEventMember.map((elem) => {
                                                        const element = { ...elem }
                                                        if (elem.id === myEventMember[index].id) {
                                                          const newPriceCategories = [...elem.eventPriceCategories]
                                                          newPriceCategories[priceIndex].quantity = newValue
                                                          element.eventPriceCategories = newPriceCategories
                                                        }
                                                        newEventMem.push(element)
                                                      })
                                                      setMyEventMember(newEventMem)
                                                    }}
                                                  />
                                                </Grid>
                                                <Grid
                                                  item xs={3}
                                                  alignItems='flex-end'
                                                >
                                                  <Box
                                                    sx={{
                                                      display: 'flex',
                                                      justifyContent: 'flex-end',
                                                      alignItems: 'center',
                                                    }}
                                                  >
                                                    <Typography
                                                      variant='body1'
                                                      align='right'
                                                    >
                                                      {formatCurrency(eventPriceCategories?.results?.find(elem => elem.id === evPriceCat.eventPriceCategoryId)?.price
                                                        ? evPriceCat.quantity * (eventPriceCategories?.results?.find(elem => elem.id === evPriceCat.eventPriceCategoryId)?.price || 0)
                                                        : 0)}
                                                    </Typography>
                                                    <IconButton
                                                      sx={{ marginTop: '0px' }}
                                                      onClick={() => {
                                                        const newEventMem: EventMember[] = []
                                                        myEventMember.map((elem) => {
                                                          const element = { ...elem }
                                                          if (elem.id === myEventMember[index].id) {
                                                            const newPriceCategories: BookedEventPriceCategory[] = []
                                                            elem.eventPriceCategories.map((price, priceIdx) => {
                                                              if (priceIndex !== priceIdx) {
                                                                newPriceCategories.push(price)
                                                              }
                                                            })
                                                            element.eventPriceCategories = newPriceCategories
                                                          }
                                                          newEventMem.push(element)
                                                        })
                                                        setMyEventMember(newEventMem)
                                                      }}
                                                    >
                                                      <CloseIcon />
                                                    </IconButton>
                                                  </Box>
                                                </Grid>
                                              </Grid>)
                                            : (
                                              <Grid container spacing={2}>
                                                <Grid item xs={6}>
                                                  <DropDownComponent
                                                    size='small'
                                                    options={getPriceOptions(myEventMember[index].memberId)}
                                                    label='Option'
                                                    multiple={false}
                                                    onChange={(value) => {
                                                      const newEventMem: EventMember[] = []
                                                      myEventMember.map((elem) => {
                                                        const element = { ...elem }
                                                        if (elem.id === myEventMember[index].id) {
                                                          const newPriceCategories = [...elem.eventPriceCategories]
                                                          newPriceCategories[priceIndex].eventPriceCategoryId = value?.key
                                                          element.eventPriceCategories = newPriceCategories
                                                        }
                                                        newEventMem.push(element)
                                                      })
                                                      setMyEventMember(newEventMem)
                                                    }}
                                                  />
                                                </Grid>
                                                <Grid item xs={3}>
                                                  <TextInputComponent
                                                    margin='none'
                                                    label='Anzahl'
                                                    required
                                                    type={TextFieldType.number}
                                                    min={1}
                                                    fullWidth
                                                    size='small'
                                                    onChange={(value?: string | number) => {
                                                      const newValue: number = parseInt(value as string, 10)
                                                      const newEventMem: EventMember[] = []
                                                      myEventMember.map((elem) => {
                                                        const element = { ...elem }
                                                        if (elem.id === myEventMember[index].id) {
                                                          const newPriceCategories = [...elem.eventPriceCategories]
                                                          newPriceCategories[priceIndex].quantity = newValue
                                                          element.eventPriceCategories = newPriceCategories
                                                        }
                                                        newEventMem.push(element)
                                                      })
                                                      setMyEventMember(newEventMem)
                                                    }}
                                                  />
                                                </Grid>
                                                <Grid
                                                  item xs={3}
                                                  alignItems='flex-end'
                                                >
                                                  <Box
                                                    sx={{
                                                      display: 'flex',
                                                      justifyContent: 'flex-end',
                                                      alignItems: 'center',
                                                    }}
                                                  >
                                                    <Typography
                                                      variant='body1'
                                                      align='right'
                                                    >
                                                      {formatCurrency(eventPriceCategories?.results?.find(elem => elem.id === evPriceCat.eventPriceCategoryId)?.price
                                                        ? evPriceCat.quantity * (eventPriceCategories?.results?.find(elem => elem.id === evPriceCat.eventPriceCategoryId)?.price || 0)
                                                        : 0)}
                                                    </Typography>
                                                    <IconButton
                                                      sx={{ marginTop: '0px' }}
                                                      onClick={() => {
                                                        const newEventMem: EventMember[] = []
                                                        myEventMember.map((elem) => {
                                                          const element = { ...elem }
                                                          if (elem.id === myEventMember[index].id) {
                                                            const newPriceCategories: BookedEventPriceCategory[] = []
                                                            elem.eventPriceCategories.map((price, priceIdx) => {
                                                              if (priceIndex !== priceIdx) {
                                                                newPriceCategories.push(price)
                                                              }
                                                            })
                                                            element.eventPriceCategories = newPriceCategories
                                                          }
                                                          newEventMem.push(element)
                                                        })
                                                        setMyEventMember(newEventMem)
                                                      }}
                                                    >
                                                      <CloseIcon />
                                                    </IconButton>
                                                  </Box>
                                                </Grid>
                                              </Grid>)}
                                        </Box>
                                      )
                                    },
                                    )}
                                  </>}
                                <Box
                                  sx={{
                                    cursor: 'pointer',
                                    display: 'flex',
                                    justifyContent: 'flex-start',
                                    alignItems: 'center',
                                    width: 'max-content',
                                  }}
                                  onClick={() => {
                                    const newEventMem: EventMember[] = []
                                    myEventMember.map((elem) => {
                                      const element = { ...elem }
                                      if (elem.id === myEventMember[index].id) {
                                        const newPriceCategories = [...elem.eventPriceCategories]
                                        newPriceCategories.push(
                                          {
                                            eventPriceCategoryId: '',
                                            quantity: 0,
                                          },
                                        )
                                        element.eventPriceCategories = newPriceCategories
                                      }
                                      newEventMem.push(element)
                                    })
                                    setMyEventMember(newEventMem)
                                  }}
                                ><AddIcon /><Typography variant='body1'>Weitere Option
                                  hinzufügen
                                            </Typography>
                                </Box>
                              </AccordionDetails>
                            </Accordion>
                            <IconButton
                              sx={{ marginTop: '12px' }}
                              onClick={() => {
                                const newEventMem: EventMember[] = []
                                myEventMember.map((elem, id) => {
                                  if (index !== id) {
                                    newEventMem.push(elem)
                                  }
                                })
                                setMyEventMember(newEventMem)
                              }}
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Box>)
                        : (
                          <DropDownComponent
                            options={getMemberOptions()}
                            label='Teilnehmer'
                            multiple={false}
                            onChange={(value) => {
                              const newEventMem = [...myEventMember]
                              newEventMem[index].memberId = value?.key

                              const member = members?.results.find(elem => elem.originId === value?.key)
                              const memberAge = member && props.data.event.eventFrom && getAgeOfMemberAtDate(member, props.data.event.eventFrom)

                              newEventMem[index].eventPriceCategories = getDefaultEventPriceCategories(activeEvent.eventPrices, memberAge)
                              setMyEventMember(newEventMem)
                            }}
                          />)}
                    </>
                  )
                },
                )}
                <Grid item xs={6}>
                  <Box
                    sx={{
                      cursor: 'pointer',
                      display: 'flex',
                      justifyContent: 'flex-start',
                      alignItems: 'center',
                      width: 'max-content',
                    }}
                    onClick={() => {
                      const newEventMem = [...myEventMember]
                      activeEvent.event.id && user && user.memberId && newEventMem.push({
                        id: 'new',
                        memberId: '',
                        registeredByMemberId: user.memberId,
                        eventPriceCategories: [],
                        eventId: activeEvent.event.id,
                        participate: true,
                        gamer: false,
                        from: props.data.event.eventFrom || {},
                        to: props.data.event.eventTo || {},
                        notes: '',
                      })
                      setMyEventMember(newEventMem)
                      setShowNewRegistration(true)
                    }}
                  ><AddIcon /><Typography variant='body1'>Weitere Person hinzufügen</Typography>
                  </Box>
                </Grid>
                <Grid item xs={6} textAlign='right'>
                  <Typography
                    sx={{ marginRight: '92px' }}
                    variant='body1'
                  ><b>Gesamt: {eventPriceCategories?.results && formatCurrency(getPriceForAllEventMembers(myEventMember, eventPriceCategories?.results))}</b>
                  </Typography>
                </Grid>
              </Grid>
            </DialogContent>
          </Stack>
          <DialogActions sx={{ p: 3 }}>
            <ButtonComponent
              color='primary'
              variant='text'
              text='Verwerfen'
              onClick={() => {
                setMyEventMember(getMyEventMember())
                setShowNewRegistration(false)
              }}
            />
            <ButtonComponent
              color='primary'
              variant='contained'
              text='Anmelden'
              onClick={() => {
                // delete member, die nicht mehr in der Liste sind
                activeEvent.eventMember.map(elem => {
                  if (!myEventMember.find(eM => eM.id === elem.id)) {
                    fetchData(basePath, getApiVersion(), fetchHeaders, Entity.eventMembers, Method.DELETE, elem.id, undefined, setEventMember, setMessage)
                  }
                })
                // erste ändern
                myEventMember.map(eventMem => {
                  if (eventMem.id !== 'new') {
                    fetchData(basePath, getApiVersion(), fetchHeaders, Entity.eventMembers, Method.PUT, eventMem.id, { eventMember: eventMem }, setEventMember, setMessage)
                  }
                })
                // dann neu anlegen
                myEventMember.map(eventMem => {
                  if (eventMem.id === 'new') {
                    fetchData(basePath, getApiVersion(), fetchHeaders, Entity.eventMembers, Method.POST, undefined, { eventMember: eventMem }, setEventMember, setMessage)
                  }
                })
                setShowNewRegistration(false)
              }}
            />
          </DialogActions>
        </Dialog>
      </>
    )
  }

  return (
    <>
      {
        downMd
          ? (
            <>
              {SingleEventContent()}
            </>)
          : (
            <Paper className={classes.paper}>
              {SingleEventContent()}
            </Paper>)
      }
    </>
  )
}

export default SingleEvent
